/* eslint-disable react-hooks/rules-of-hooks */
import { Button, ExternalLink, Heading, Layout, MainLoader, Modal, ModalContent, ModalFooter, ModalHeader, Notification, removeMainLoader, SlidingMenu, Text } from "@netapp/bxp-design-system-react";
import classNames from "classnames";
import _ from "lodash";
import AppNotifications from "modules/app/components/AppNotifications";
import CategoriesNav from "modules/app/components/headerMenus/leftNav/CategoriesNav";
import SelectedServiceNav from "modules/app/components/headerMenus/leftNav/components/SelectedServiceNav";
import { MonitorCurrentConnector } from "modules/current-connector";
import React, { memo, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, Route, Switch } from 'react-router-dom';
import { isAllowed } from "utils/permissions";
import { FSX_ADMINISTRATION_LEGACY_PREFIX, LegacyFsxAdministrationRedirect } from "utils/legacyFsxAdministrationRedirect";
import useCurrentService from "utils/useCurrentService";
import styles from "./app.module.scss";
import { appComponents as i18nComponentsNamespace } from "./i18n/namespace";
import { ReactComponent as NoLicenseIcon } from './images/aide-nolicense.svg';
import { ReactComponent as NoServicesIcon } from './images/aide-noservices.svg';
import ServiceIntegration from "./modules/app/ServiceIntegration";
import AppBootPerformanceLogger from "./modules/app/components/AppBootPerformanceLogger";
import DefaultRoute from "./modules/app/components/DefaultRoute";
import InactiveConnectorDialog from "./modules/app/components/InactiveConnectorDialog";
import MainErrorBoundary from "./modules/app/components/MainErrorBoundary";
import MainHeader from "./modules/app/components/MainHeader";
import { Forbidden } from "./modules/app/components/NoAccessComponents";
import SlidingManagementWidget from './modules/app/components/SlidingManagementWidget';

const SelectedServiceNavWrapper = memo(() => {
    const isShowingCategoriesNav = useSelector(state => state.menu.activeWidget === 'leftNav');
    return <SelectedServiceNav isShowingCategoriesNav={isShowingCategoriesNav} />;
});


const SlidingMenuWrapper = memo(({ lastNavRef, mainHeaderRef, onCategoryMenuItemClicked }) => {
    const dispatch = useDispatch();
    const isShowingCategoriesNav = useSelector(state => state.menu.activeWidget === 'leftNav');

    // Somehow stopping the propagation at bubbling phase doesn't work, so we use capture phase.
    // Add global event interceptor to prevent SlidingMenu from closing when clicking in flyout menu.
    useEffect(() => {
        if (!isShowingCategoriesNav) {
            return;
        }

        const handleFlyoutMenuClick = (e) => {
            const isInFlyoutMenu = e.target.closest('[data-flyout-menu]');

            if (isInFlyoutMenu) {
                // Check if the click is on an interactive element (buttons, links, service cards)
                const isOnInteractiveElement = e.target.closest('[data-service-card], [data-favorite-button]');

                if (!isOnInteractiveElement) {
                    // Only prevent if clicking on empty space, not on interactive elements
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    e.preventDefault();
                    return false;
                }
            }
        };

        // Use capture phase to intercept before SlidingMenu's event handlers
        document.addEventListener('click', handleFlyoutMenuClick, true);
        document.addEventListener('mousedown', handleFlyoutMenuClick, true);

        return () => {
            document.removeEventListener('click', handleFlyoutMenuClick, true);
            document.removeEventListener('mousedown', handleFlyoutMenuClick, true);
        };
    }, [isShowingCategoriesNav]);

    const handleCloseTrigger = useCallback(() => {
        dispatch({
            type: "MENU:CLOSE-WIDGET"
        });
    }, [dispatch]);

    return (
        <SlidingMenu
            isActive={isShowingCategoriesNav}
            isSlidingFromLeft={true}
            top={72}
            closeTrigger={handleCloseTrigger}
            className={styles['sliding-menu-left-nav']}
        >
            <CategoriesNav ref={lastNavRef} mainHeaderRef={mainHeaderRef} onCategoryMenuItemClicked={onCategoryMenuItemClicked} isShowingCategoriesNav={isShowingCategoriesNav} />
        </SlidingMenu>
    );
});

const TimeoutModal = () => {
    const { t } = useTranslation(i18nComponentsNamespace);
    const dispatch = useDispatch();
    const renew = () => {
        dispatch({
            type: "AUTH:RENEW-SSO"
        })
    };

    return <Modal closeTrigger={renew}>
        <ModalHeader>
            {t("authentication_renew")}
        </ModalHeader>
        <ModalContent>
            {t("session_expired")}
        </ModalContent>
        <ModalFooter>
            <Button onClick={renew}>{t("refresh")}</Button>
        </ModalFooter>
    </Modal>;
};
export const NoAccessMessage = ({headingKey, subtextKey, learnMoreLink}) => {
    const { t } = useTranslation(i18nComponentsNamespace);
    return <>
        <Layout.Container>
            <Layout.Grid centerContent>
                <Layout.GridItem lg={12} style={{ flex: "0 0 350px" }}>
                    {!learnMoreLink && <NoLicenseIcon color='text' style={{ marginTop: 120, width: 350 }} />}
                    {learnMoreLink && <NoServicesIcon color='text' style={{ marginTop: 100, width: 350 }} />}
                </Layout.GridItem>
                <Layout.GridItem lg={12}>
                    <Heading level={20} style={{ marginTop: 16, textAlign: "center" }} bold>{t(headingKey)}</Heading>
                    <Text style={{ marginTop: 16, textAlign: 'center' }}>{t(subtextKey)}</Text>
                    {learnMoreLink && <Text style={{ marginTop: 16, textAlign: 'center' }}><ExternalLink variant="text" trackLabel="no-feature-flags" href={learnMoreLink} includeArrow={true} dataTestId="no-features-error-link">
                        {t("learn_more")}
                    </ExternalLink></Text>}
                </Layout.GridItem>
            </Layout.Grid>
        </Layout.Container>
    </>
};
const AIDPShell = () => {
    const lastNavRef = useRef(null);
    const mainHeaderRef = useRef(null);

    const { t } = useTranslation(i18nComponentsNamespace);
    const dispatch = useDispatch();

    const isLoadingServices = useSelector(state => state.app.isLoadingServices);
    const isGuardrailsEnabled = useSelector(state => state.features.active['NetApp.DataMap/*'] ?? false);
    const isCuratorEnabled = useSelector(state => state.features.active['NetApp.AIDataCurator/*'] ?? false);
    const isDataExplorerEnabled = useSelector(state => state.features.active['NetApp.AIDataExplorer/*'] ?? false);
    const isSettingsEnabled = useSelector(state => state.features.active['NetApp.AISettings/*'] ?? false);
    const serviceIntegrationPaths = useSelector(state => state.app.serviceIntegrationPaths);
    const { isAuthenticated, renewTimeout, status } = useSelector(state => state.auth);
    const { forbidden, isChangedAgent, isTransitionFromUnselectedToSelected, permissions } = useSelector(state => state.tenancy);

    const isFeaturesInitialized = useSelector(state => state.features.isInitialized);
    const selectedOrg = useSelector(state => state.tenancy.selectedOrg);
    const selectedOrgRules = selectedOrg?.rules || [];

    const currentService = useCurrentService();
    const [containsGuardRails, setContainsGuardRails] = useState(undefined);
    const [containsCurator, setContainsCurator] = useState(undefined);
    const [containsSettings, setcontainsSettings] = useState(undefined);

    // When NO-LICENSE, treat curator and guardrails as disabled — DataExplorer is the only accessible service
    const isNoLicense = status === 'NO-LICENSE';
    const canAccessDataExplorer = !isLoadingServices && (isNoLicense || isDataExplorerEnabled);

    useEffect(() => {
        if (!isLoadingServices && isAuthenticated) { //checks isAuthenticated cause feature flag manual toggles get saved with the userId
            dispatch({
                type: "APP:INITIALIZE-FEATURES"
            })
        }
    }, [isLoadingServices, dispatch, isAuthenticated]);

    useEffect(() => {
        if (isFeaturesInitialized) {
            dispatch({
                type: "MENU:INITIALIZE"
            })
            dispatch({
                type: "TENANCYV4:AIDE-PERMISSIONS"
            })
        }
    }, [isFeaturesInitialized]);

    useEffect(() => {
        if (selectedOrg?.rules === undefined) return; // org not yet loaded ({} has no rules) — wait
        if (isNoLicense) {
            // NO-LICENSE users must not access guardrails or curator regardless of org rules
            setContainsCurator(false);
            setContainsGuardRails(false);
        } else {
            // selectedOrgRules may be [] (loaded but empty) — isAllowed returns false, redirects to data-explorer
            setContainsCurator(isCuratorEnabled && isAllowed(selectedOrgRules, "aidp_data_explorer", "access"));
            setContainsGuardRails(isGuardrailsEnabled && isAllowed(selectedOrgRules, "aidp_data_guardrails", "access"));
        }
        // data-settings: always check aidp_data_guardrails permission — no license check, feature always on
        setcontainsSettings(isAllowed(selectedOrgRules, "aidp_data_guardrails", "access"));
    }, [selectedOrg, isGuardrailsEnabled, isCuratorEnabled, isSettingsEnabled, isNoLicense]);

    function onCategoryMenuItemClicked() {
        dispatch({
            type: "MENU:CLOSE-WIDGET"
        });
    }

    const showTabs = (isNoLicense ? canAccessDataExplorer : !_.isUndefined(containsGuardRails)) && (isGuardrailsEnabled || isCuratorEnabled || canAccessDataExplorer || isSettingsEnabled);
    const showLoader = _.isEmpty(permissions) && !forbidden;
    const showLeftNav = true;
    const showServicesError = !showLoader && !isGuardrailsEnabled && !isCuratorEnabled && !canAccessDataExplorer && !isSettingsEnabled;

    // "resolved" = either the feature is off (org rules not needed),
    // or org rules have loaded and set the state (no longer undefined)
    const guardrailsResolved = !isGuardrailsEnabled || containsGuardRails !== undefined;
    const curatorResolved = !isCuratorEnabled || containsCurator !== undefined;
    /*
     For NO-LICENSE: containsGuardRails and containsCurator are explicitly set to false,
     so redirect directly to data-explorer without waiting for guardrails/curator resolution.
     Note: dataSettingsResolved is not needed here — data-settings only redirects when !shouldRedirectToDataExplorer (canAccessDataExplorer=false), so the two are mutually exclusive.
    */
    const shouldRedirectToDataExplorer = isNoLicense
        ? canAccessDataExplorer
        : guardrailsResolved && curatorResolved && !containsGuardRails && !containsCurator && canAccessDataExplorer;

    return <div className={classNames((currentService?.isShowingSelectedServiceNav) ? styles.appBase : styles.noNavBase, { [styles['initializing']]: !showLeftNav })}>
        {process.env.REACT_APP_ENDPOINTS !== "prod" && <AppBootPerformanceLogger />}
        {/* Disable the left nav hamburger when there is no-license and user don't have access to settings app (non-admin),
        Even when no-license, admin users can access settings */}
        <MainHeader ref={mainHeaderRef} navRef={lastNavRef} noOrganizationForUser={(status === 'NO-LICENSE' && containsSettings === false) || showServicesError} />
        {showLeftNav && <>
            {currentService?.isShowingSelectedServiceNav && <SelectedServiceNavWrapper />}
        </>}
        <SlidingMenuWrapper lastNavRef={lastNavRef} mainHeaderRef={mainHeaderRef} onCategoryMenuItemClicked={onCategoryMenuItemClicked} />
        <div className={styles.doodads}>
            {isChangedAgent && <InactiveConnectorDialog />}
            {renewTimeout && <TimeoutModal />}
            <SlidingManagementWidget />
            <MonitorCurrentConnector />
            {isTransitionFromUnselectedToSelected && <Notification type="info" style={{ width: 820, position: "fixed", bottom: 63, left: "50%", transform: "translateX(-50%)" }}>
                {t("active_connector")}
            </Notification>}
        </div>

        <div className={styles.container}>
            {forbidden && <Forbidden />}
            <MainErrorBoundary>
                <Suspense fallback={<MainLoader />}>
                    <Switch>
                        <Route>
                            <>
                                {/* Services loaded but permissions (org/tenancy) still resolving */}
                                {!isLoadingServices && showLoader && <MainLoader />}
                                {showServicesError && <NoAccessMessage headingKey="aide_no_features_enabled" subtextKey="aide_tech_preview_disabled" learnMoreLink={"https://docs.netapp.com/us-en/ai-data-engine/"}/>}
                                {/* Initial service discovery in progress — showTabs is false */}
                                {isLoadingServices && <MainLoader />}
                                {/* EXPIRED-LICENSE screen */}
                                {!isLoadingServices && status === 'EXPIRED-LICENSE' && <NoAccessMessage headingKey="aide_license_expired" subtextKey="aide_renew_license" />}
                                {showTabs && <Switch>
                                    <Route path={FSX_ADMINISTRATION_LEGACY_PREFIX} render={({ location }) => <LegacyFsxAdministrationRedirect location={location} />} />
                                    {/* Fail-safe: NO-LICENSE users who directly navigate to guardrails/curator see license error instead of the feature page */}
                                    {isNoLicense && <Route path="/console/data-guardrails" render={() => <NoAccessMessage headingKey="aide_no_license" subtextKey="aide_contact_admin" />} />}
                                    {isNoLicense && <Route path="/console/data-curator" render={() => <NoAccessMessage headingKey="aide_no_license" subtextKey="aide_contact_admin" />} />}
                                    {/* Fail-safe: feature-disabled users who directly navigate to guardrails/curator see feature-disabled error instead of the page */}
                                    {!isGuardrailsEnabled && <Route path="/console/data-guardrails" render={() => <NoAccessMessage headingKey="aide_no_features_enabled" subtextKey="aide_tech_preview_disabled" learnMoreLink={"https://docs.netapp.com/us-en/ai-data-engine/"} />} />}
                                    {!isCuratorEnabled && <Route path="/console/data-curator" render={() => <NoAccessMessage headingKey="aide_no_features_enabled" subtextKey="aide_tech_preview_disabled" learnMoreLink={"https://docs.netapp.com/us-en/ai-data-engine/"} />} />}
                                    {!_.isEmpty(serviceIntegrationPaths) && <Route path={serviceIntegrationPaths} render={() => <ServiceIntegration />} />}
                                    {containsGuardRails && <Redirect to={{ pathname: "/console/data-guardrails", hash: window.location.hash }} />}
                                    {!containsGuardRails && containsCurator && <Redirect to={{ pathname: "/console/data-curator", hash: window.location.hash }} />}
                                    {shouldRedirectToDataExplorer && <Redirect to={{ pathname: "/console/data-explorer", hash: window.location.hash }} />}
                                    {/* data-settings (administration) has no boot-time redirect — data-explorer is always on and is the default.
                                        NO-LICENSE users with settings access also go to data-explorer. Service integration handles access control within the page. 
                                        Commenting the below code for now, because when MDE license comes, we might need to handle this re-direction */}
                                    {/* {!shouldRedirectToDataExplorer && containsSettings && <Redirect to={{ pathname: "/console/administration", hash: window.location.hash }} />} */}
                                    <Route path="/:path" render={() => <DefaultRoute />} />
                                </Switch>}
                            </>
                        </Route>
                    </Switch>
                </Suspense>
            </MainErrorBoundary>
        </div>
    </div>
};

const AIDPApp = React.memo(() => {
    const appState = useSelector(state => state.app.appState);
    removeMainLoader();
    return (
        <>
            {appState === 'loading' && <MainLoader />}
            <AppNotifications />
            {appState === 'ready' && <AIDPShell />}
        </>
    );
});

export default AIDPApp;